import { useState, useCallback, useEffect } from 'react';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalization } from '@/hooks/useLocalization';
import { translateDynamicContent } from '@/i18n';
import { AuthCheckbox } from './AuthControls';
import { CloseIcon } from './AuthIcons';
import styles from './PrivacyConsent.module.css';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { appFontEmbedHtml } from '@/services/tenant/tenantFont';

/**
 * PrivacyConsent — checkbox + terms/privacy HTML text.
 *
 * Mirrors Flutter TermsAndConditionsTextWidget + CandidatePrivacyNotice.
 *
 * The consent text is tenant HTML from contentSettings.termAndConditions.content (nothing
 * else is shown when it is empty). A link with href "dialog" or "#" opens the notice from
 * contentSettings.privacyPolicy.content in a dialog; any other link opens in a new tab, as
 * HtmlWidget's onTapUrl falls back to launching it. The notice is a full HTML document with
 * its own styles, so it is rendered in a sandboxed iframe (scripts and forms stay blocked;
 * same-origin only so it can load the app font). It is shown in the app font, as Flutter's
 * HtmlWidget shows it, even where the notice names its own font.
 *
 * Tenants may author both as keys into the language file's `dynamicContent` map
 * (e.g. "termAndConditionsHTML"), so both are translated like Flutter's
 * appLocalization.translateMap(dynamicContent).getValue(...).
 *
 * Flutter source files:
 *   - presentation/common/candidate_privacy_notice/terms_and_condition_text_widget.dart
 *   - presentation/common/candidate_privacy_notice/candidate_privacy_notice.dart
 *   - domain/core/constants/string_constants.dart (CandidatePrivacyNoticeConstant)
 */

interface PrivacyConsentProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * The notice document with links opening in a new tab instead of inside the iframe, and the app
 * font (appFontEmbedHtml).
 */
function preparePrivacyDocument(html: string): string {
  const head = `<base target="_blank">${appFontEmbedHtml()}`;
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => m + head);
  // No <head> (e.g. "<!DOCTYPE html><html><body>…"): add one inside <html>, keeping the doctype first
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${head}</head>`);
  if (/^\s*<!doctype[^>]*>/i.test(html)) return html.replace(/^\s*<!doctype[^>]*>/i, (m) => m + head);
  return head + html;
}

export function PrivacyConsent({ checked, onChange }: PrivacyConsentProps) {
  const { t } = useLocalization();
  const { content } = useTenantStore();
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const termAndConditionsHtml = translateDynamicContent(
    content?.contentSettings?.termAndConditions?.content ?? '',
  );
  const privacyPolicyHtml = translateDynamicContent(content?.contentSettings?.privacyPolicy?.content ?? '');

  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const link = (e.target as HTMLElement).closest('a');
    if (!link) return;
    // Also stops the surrounding <label> from toggling the checkbox
    e.preventDefault();
    const href = link.getAttribute('href') ?? '';
    if (href === 'dialog' || href === '#') {
      setShowPrivacyModal(true);
    } else if (href) {
      window.open(link.href, '_blank', 'noopener,noreferrer');
    }
  }, []);

  useEffect(() => {
    if (!showPrivacyModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowPrivacyModal(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showPrivacyModal]);

  return (
    <>
      <div className={styles.consentRow}>
        <AuthCheckbox
          checked={checked}
          onChange={onChange}
          aria-label="Agree to privacy policy and terms"
        />
        <div
          className={styles.consentText}
          onClick={handleLinkClick}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(termAndConditionsHtml) }}
        />
      </div>

      {showPrivacyModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowPrivacyModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t('Candidate Privacy Notice')}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => setShowPrivacyModal(false)}
              aria-label="Close privacy notice"
            >
              <CloseIcon size={24} />
            </button>
            <iframe
              className={styles.privacyFrame}
              title={t('Candidate Privacy Notice')}
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
              srcDoc={preparePrivacyDocument(privacyPolicyHtml)}
            />
          </div>
        </div>
      )}
    </>
  );
}
